import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, Grid, LinearProgress, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { Fighter } from '../../../../../corelogic/domain/models/fighter.model';
import {
  defaultFighterInfo,
  FighterInfo,
} from '../../../../../corelogic/domain/models/fighterInfo.model';
import { checkSelectedSeasonFights } from '../../../../../corelogic/domain/utils/checkSelectedSeasonFights.utils';
import { createFighterInfo } from '../../../../../corelogic/domain/utils/fighterInfo.utils';
import { formatFightersForBackend } from '../../../../../corelogic/domain/utils/saveUserFighters.utils';
import {
  calculateFightsRemaining,
  calculateTotalPoints,
} from '../../../../../corelogic/domain/utils/selector.utils';
import { store } from '../../../../../corelogic/state/store';
import { UPSERT_USER_SEASON_SUCCESS } from '../../../../../corelogic/state/userSeason/userSeason.action.type';
import { useFighterState } from '../../hooks/useFighterState';
import { useFightState } from '../../hooks/useFightState';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import { useSeasonFightsState } from '../../hooks/useSeasonFightsState';
import { useSeasonState } from '../../hooks/useSeasonState';
import { useUserSeasonState } from '../../hooks/useUserSeasonState';
import { SeasonFightsService } from '../../services/SeasonFightsService';
import { UserSeasonService } from '../../services/UserSeasonService';
import { ConfirmationModal } from '../ConfirmationModal/ConfirmationModal';
import SeasonSelector from '../SeasonSelector/SeasonSelector';
import ReusableAlert from '../Shared/ReusableAlert/ReusableAlert';
import { SuccessModal } from '../Shared/SuccessModal/SuccessModal';
import FighterPicker from './FighterPicker/FighterPicker';
import SelectorCard from './SelectorCard/SelectorCard';
import SelectorInfoPanel from './SelectorInfoPanel/SelectorInfoPanel';
import SelectorModal from './SelectorModal/SelectorModal';
import './TeamSelector.css';

/* TODO: 
- Change Seasons table to include fight results so i can do point calculations + last wins
*/

const TeamSelector: React.FC = () => {
  /* State Variables for Seasons and Fighters */
  const { fighters } = useFighterState();
  const { fights: allFights, isLoading: isFightsLoading } = useFightState();
  const { seasons } = useSeasonState();

  const {
    seasonId: userSeasonId,
    fighters: userSeasonFighters,
    isLoading: isUserSeasonLoading,
  } = useUserSeasonState();

  const {
    seasonId: seasonFightsId,
    fights: seasonFights,
    isLoading: isSeasonFightsLoading,
  } = useSeasonFightsState();

  // Use seasonFightsId as the "primary" ID for operations
  const selectedSeasonId = seasonFightsId;

  /* Variables for the snackbar, success modal, and navigation guard */
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  /* Variables for the temporary fighter info array & saving */
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<
    null | (() => void)
  >(null);
  const [fighterInfoArray, setFighterInfoArray] = useState<FighterInfo[]>([]);

  /* Other variables */
  const [pointsValue, setPointsValue] = useState<number>(0);
  const [fightersRemaining, setFightersRemaining] = useState<number>(0);
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [modalFighterInfo, setModalFighterInfo] = useState<Fighter | null>(
    null,
  );
  const [modalSelectedFighterInfo, setModalSelectedFighterInfo] =
    useState<Fighter | null>(null);
  const [modalFightId, setModalFightId] = useState<string | null>(null);

  /* Verify seasonIds match for both user season and season fights */
  useEffect(() => {
    if (
      userSeasonId &&
      seasonFightsId &&
      userSeasonId !== seasonFightsId &&
      !isSeasonFightsLoading &&
      seasonFights.length > 0
    ) {
      // If we have valid seasonFights but userSeason doesn't match sync
      console.warn('Season ID mismatch between user season and season fights');
      UserSeasonService.retrieveUserSeason(seasonFightsId);
    }
  }, [userSeasonId, seasonFightsId, isSeasonFightsLoading, seasonFights]);

  /* Function to handle season change */
  const handleSeasonChange = (seasonId: string) => {
    // Don't show error if fights are still loading
    if (isFightsLoading) {
      console.log('Fights still loading, proceeding with season change...');
      SeasonFightsService.retrieveSeasonFights(seasonId);
      UserSeasonService.retrieveUserSeason(seasonId);
      return;
    }

    const fightsInSeason = checkSelectedSeasonFights(
      seasons,
      allFights,
      seasonId,
    );

    if (!fightsInSeason) {
      setSnackbarText(
        `No Events scheduled for this season. Please select another season.`,
      );
      setOpenSnackbar(true);
      return;
    }

    // Continue with season change if there are fights
    if (hasUnsavedChanges && !showSuccessModal) {
      setConfirmationModal({
        isOpen: true,
        title: 'Unsaved Changes',
        message:
          'You have unsaved changes. Are you sure you want to switch seasons? Your changes will be lost.',
        onConfirm: () => {
          SeasonFightsService.retrieveSeasonFights(seasonId);
          UserSeasonService.retrieveUserSeason(seasonId);
          setHasUnsavedChanges(false);
          setPendingNavigation(null);
        },
      });
      return;
    }
    // if in fights there is a fight for tht seaosn call, otherwise deport
    SeasonFightsService.retrieveSeasonFights(seasonId);
    UserSeasonService.retrieveUserSeason(seasonId);
  };

  /* Fighter Info array stores the info for your currently selected fighters - it starts with the users state but is modifiable and won't change the state until you save your team */
  const initializeFighterCards = () => {
    const newFighterCards = Array(8)
      .fill(null)
      .map((_, index) => ({
        ...defaultFighterInfo(),
        CardId: index + 1,
      }));

    if (!seasonFights || seasonFights.length === 0) {
      return;
    }

    // Add the user's season fighters to the new fighter cards
    userSeasonFighters?.forEach((userSeasonFighter) => {
      // Check if the position is valid
      if (
        !userSeasonFighter.position ||
        userSeasonFighter.position < 1 ||
        userSeasonFighter.position > 8
      ) {
        console.warn(
          'Invalid position for user season fighter:',
          userSeasonFighter,
        );
        return;
      }

      // Find the fighter details
      const fighterDetails = fighters.find(
        (f) => f.id === userSeasonFighter.fighterId,
      );

      if (!fighterDetails) {
        console.error(
          `Fighter not found for position ${userSeasonFighter.position}:`,
          userSeasonFighter,
        );
        return;
      }

      // Find the season fight details
      const fight = seasonFights.find(
        (f) => f.fight_id === userSeasonFighter.fightId,
      );

      if (!fight) {
        console.error(
          `Fight not found for position ${userSeasonFighter.position}:`,
          userSeasonFighter,
        );
        return;
      }

      // Create the new fighter info with utils function createFighterInfo
      const result = createFighterInfo(
        userSeasonFighter.position,
        fighterDetails,
        '', // Empty string for image, will be handled by SelectorCard
        fight,
        userSeasonFighter.points || 0,
      );

      // Check if the result is an error string
      if (typeof result === 'string') {
        console.error(
          `Error creating fighter info for position ${userSeasonFighter.position}:`,
          result,
        );
        return;
      }

      // If we get here, result is a valid FighterInfo object
      newFighterCards[userSeasonFighter.position - 1] = result;
    });
    return newFighterCards;
  };

  /* Effect to initialize the fighter info array */
  useEffect(() => {
    // Initialize on season change
    const newFighterCards = initializeFighterCards();
    if (newFighterCards) {
      setFighterInfoArray(newFighterCards);
    }
  }, [
    userSeasonId,
    seasonFightsId,
    userSeasonFighters,
    fighters,
    seasonFights,
  ]);

  /* Update currently selected fighter info in the pop up modal when you choose a fighter to switch */
  useEffect(() => {
    if (selectedCardId !== null) {
      const selectedFighterInfo = fighterInfoArray.find(
        (info) => info.CardId === selectedCardId,
      );
      if (selectedFighterInfo) {
        const fighterInfo = fighters.find(
          (f) => f.id === selectedFighterInfo.FighterId,
        );
        // If the fighter exists, set the fighter info to pass into modal
        if (fighterInfo) {
          setModalSelectedFighterInfo(fighterInfo);
        }
      }
    }
  }, [selectedCardId, fighterInfoArray, fighters]);

  /* When you select a current fighter in your Roster, it updates what is currently selected for upcoming logic */
  const handleCardButtonClick = (cardId: number) => {
    if (selectedCardId === cardId) {
      setSelectedCardId(null); // Deselect if the same card is clicked again
    } else {
      setSelectedCardId(cardId); // Select the card
    }
  };

  /* Modal actions, including opening it, closing it, and also what happens when you save your selected fighters */
  const handleOpenModal = (
    fighterId: string,
    fightId: string,
    fightDate: Date,
    position?: number,
  ) => {
    const fightDateObject = new Date(fightDate);
    if (fightDateObject < new Date()) {
      setSnackbarText(
        'This event has completed - please pick from another Season!',
      );
      setOpenSnackbar(true);
      return;
    }

    // Only set selectedCardId if it's not already set and position is provided
    if (selectedCardId === null && position !== undefined) {
      setSelectedCardId(position);
    }

    // If your selected fighter is in a title fight...
    const isPickerFightIdTitle =
      seasonFights.find((fight) => fight.fight_id === fightId)?.title_fight ===
      'T';

    // If the fighter in the fight already has points, you can't swap
    const selectedFighterInfo = fighterInfoArray.find(
      (info) => info.CardId === selectedCardId,
    );
    const isPickerFightIdPoints =
      selectedFighterInfo &&
      selectedFighterInfo.FighterId !== '' &&
      selectedFighterInfo.Points !== 0;

    // And your selected fighter to replace is a title fighter...
    const isSelectedFighterTitle =
      fighterInfoArray.find((info) => info.CardId === selectedCardId)?.Title ===
      'T';

    // And you have already selected 2 title fighters...
    const titleFightersLimitReached =
      fighterInfoArray.filter((fighter) => fighter.Title === 'T').length >= 2;

    // Then you can swap, otherwise you cannot swap
    if (
      isPickerFightIdTitle &&
      !isSelectedFighterTitle &&
      titleFightersLimitReached
    ) {
      setSnackbarText(
        'Title Fighter Limit Reached! Please pick another fighter.',
      );
      setOpenSnackbar(true);
      setSelectedCardId(null);
      return;
    }

    // If the fighter in the fight already has points, prevent the user from swapping
    if (isPickerFightIdPoints) {
      setSnackbarText(
        'Fighter already has points! Please pick another fighter.',
      );
      setOpenSnackbar(true);
      setSelectedCardId(null);
      return;
    }

    // Find the fighter to swap in within the fighters state using fighterId
    const fighter = fighters.find((f) => f.id === fighterId);
    if (!fighter) {
      setSnackbarText('Selected fighter not found - please try again!');
      setOpenSnackbar(true);
      return;
    }

    // check against each fighter in the fighterInfoArray and see if fighter.id AND fight.id matches
    const fighterInfo = fighterInfoArray.find(
      (info) => info.FighterId === fighter?.id && info.FightId === fightId,
    );
    if (!fighterInfo) {
      setModalFighterInfo(fighter);
      setModalFightId(fightId);
      setOpenModal(true);
    } else {
      setSnackbarText(
        'You have already selected this fighter! Please pick a different fighter.',
      );
      setOpenSnackbar(true);
      setSelectedCardId(null);
    }
  };

  /* Close the modal for the fighter picker */
  const handleCloseModal = () => {
    setOpenModal(false);
    setModalFighterInfo(null);
    setSelectedCardId(null);
    setModalFightId(null);
    setModalSelectedFighterInfo(null);
  };

  /* Swap fighters into the temporary fighter info array */
  const handleSwapFighters = () => {
    if (!modalFighterInfo || selectedCardId === null) return;

    // Find the season fight for the selected fighter
    const seasonFight = seasonFights.find(
      (fight) => fight.fight_id === modalFightId,
    );

    if (!seasonFight) {
      setSnackbarText("Fighter not found in selected season's fights");
      setOpenSnackbar(true);
      handleCloseModal();
      return;
    }

    // Create new fighter info
    const newFighterInfo = createFighterInfo(
      selectedCardId,
      modalFighterInfo,
      '', // Empty string for image, will be handled by SelectorCard
      seasonFight,
      0, // New selections don't have points
    );

    // Check if the result is an error string
    if (typeof newFighterInfo === 'string') {
      setSnackbarText(`Error: ${newFighterInfo}`);
      setOpenSnackbar(true);
      handleCloseModal();
      return;
    }

    // Update the array
    setFighterInfoArray((prevArray) => {
      const updatedArray = prevArray.map((info) =>
        info.CardId === selectedCardId ? newFighterInfo : info,
      );
      return updatedArray;
    });

    setHasUnsavedChanges(true);
    setPendingNavigation(null);
    console.log('Setting hasUnsavedChanges to true');
    handleCloseModal();
  };

  /* Used for notification if the fighters selected are the same */
  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  /* Listen for successful user season update to show success modal */
  useEffect(() => {
    const subscription = store.action$.subscribe((action) => {
      if (action.type === UPSERT_USER_SEASON_SUCCESS) {
        setShowSuccessModal(true);
        setHasUnsavedChanges(false);
        setPendingNavigation(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  /* Navigation guard effect to show confirmation dialog if unsaved changes */
  useNavigationBlocker({
    when: hasUnsavedChanges && !showSuccessModal && !openModal,
    onBlock: (navigateTo) => {
      setConfirmationModal({
        isOpen: true,
        title: 'Unsaved Changes',
        message:
          'You have unsaved changes. Are you sure you want to leave? Your changes will be lost.',
        onConfirm: () => {
          // now use the state to store the navigation function and where it's going to redirect to
          // will be triggered in the useeffect below
          setHasUnsavedChanges(false);
          setPendingNavigation(() => navigateTo);
        },
      });
    },
  });

  /* useEffect is triggered as soon as you hit confirm in the modal, and then will navigate immediately */
  useEffect(() => {
    if (pendingNavigation && !hasUnsavedChanges) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  }, [pendingNavigation, hasUnsavedChanges]);

  /* Event listener for page unload to show confirmation dialog if unsaved changes */
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  /* Save changes to the user season */
  const handleSaveChanges = () => {
    if (!hasUnsavedChanges) {
      setSnackbarText('No unsaved changes detected');
      setOpenSnackbar(true);
      return;
    }

    if (!selectedSeasonId) {
      return;
    }

    // Format the entire team for the backend send
    const formattedTeam = formatFightersForBackend(
      fighterInfoArray.filter(
        (fighter) => fighter.FightId !== 'default' && fighter.FightId !== '',
      ),
    );

    if (formattedTeam.length === 0) {
      setSnackbarText('No changes to save');
      setOpenSnackbar(true);
      setHasUnsavedChanges(false);
      return;
    }

    UserSeasonService.upsertUserSeason(selectedSeasonId, formattedTeam);
  };

  /* Reset changes to the user season */
  const handleResetChanges = () => {
    if (!hasUnsavedChanges) {
      setSnackbarText('No changes to reset');
      setOpenSnackbar(true);
      return;
    }

    setConfirmationModal({
      isOpen: true,
      title: 'Reset Changes',
      message:
        'Are you sure you want to reset all changes? This cannot be undone.',
      onConfirm: () => {
        const newFighterCards = initializeFighterCards();
        if (newFighterCards) {
          setFighterInfoArray(newFighterCards);
          setHasUnsavedChanges(false);
          setPendingNavigation(null); // Reset navigation confirmation after reset
          setSnackbarText('Changes have been reset without saving');
          setOpenSnackbar(true);
        }
      },
    });
  };

  /* This effect is used to calculate points and fights left */
  useEffect(() => {
    const totalPoints = calculateTotalPoints(fighterInfoArray);
    setPointsValue(totalPoints);

    const fightsLeft = calculateFightsRemaining(fighterInfoArray);
    setFightersRemaining(fightsLeft);
  }, [fighterInfoArray]);

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: { xs: 'auto', md: '100%' },
        padding: { xs: '10px 5px', md: '10px 30px 20px 10px' },
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.9)), url('/viewer_octagon.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        overflow: { xs: 'visible', md: 'hidden' },
      }}
    >
      {/* Left Side - Grid Section */}
      <Box
        component={motion.div}
        variants={container}
        initial="hidden"
        animate="show"
        sx={{
          width: { xs: '100%', md: '60%' },
          height: { xs: 'auto', md: '100%' },
          display: 'flex',
          flexDirection: 'column',
          overflow: { xs: 'visible', md: 'hidden' },
          marginBottom: { xs: '40px', md: 0 },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            marginBottom: '10px',
            flexShrink: 0,
            gap: '20px',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Rubik Mono One',
              textAlign: 'left',
              fontSize: 'clamp(1rem, 1.5vw, 2rem)',
              flex: '1',
            }}
          >
            Select Your Fantasy Team!
          </Typography>
          <Box sx={{ width: 'clamp(140px, 10vw, 160px)', marginRight: '12px' }}>
            <SeasonSelector
              selectedSeasonId={selectedSeasonId}
              onSeasonChange={handleSeasonChange}
            />
          </Box>
        </Box>
        {/* Grid */}
        <Box sx={{ flex: 1, overflow: 'auto', height: '92%' }}>
          <Grid
            container
            spacing={{ xs: 2, sm: 3, md: 6 }}
            sx={{
              width: '100%',
              height: '100%',
              marginBottom: '10px',
              paddingRight: { xs: 0, md: '20px' },
            }}
          >
            {fighterInfoArray.map((fighterInfo, index) => (
              <Grid
                item
                xs={6}
                sm={4}
                md={3}
                key={index}
                sx={{
                  height: {
                    xs: '200px',
                    sm: 'clamp(220px, 30vh, 300px)',
                    md: 'clamp(180px, 40vh, 500px)',
                  },
                }}
              >
                <SelectorCard
                  cardId={fighterInfo.CardId}
                  name={fighterInfo.FighterName}
                  opponent={fighterInfo.Opponent}
                  fightDate={fighterInfo.FightDate}
                  points={fighterInfo.Points}
                  selectedCardId={selectedCardId}
                  title={fighterInfo.Title}
                  onCardClick={() => handleCardButtonClick(fighterInfo.CardId)}
                  isLoading={isUserSeasonLoading || isSeasonFightsLoading}
                  firstname={fighterInfo.Firstname}
                  lastname={fighterInfo.Lastname}
                  dob={fighterInfo.Dob}
                  gender={fighterInfo.Gender}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
        {/* Info Panels */}
        <Box
          sx={{
            paddingLeft: { xs: '5px', sm: '10px' },
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            gap: { xs: '8px', sm: '16px' },
            flexShrink: 1,
            flexWrap: 'nowrap',
            height: '8%',
            minHeight: { xs: '32px', sm: '32px' },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '6px',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background:
                  'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                animation: 'sheen 3s infinite',
                zIndex: 1,
              },
              '@keyframes sheen': {
                '0%': { left: '-100%' },
                '100%': { left: '100%' },
              },
            }}
          >
            <SelectorInfoPanel text={`🏆 Total Points: ${pointsValue}`} />
          </Box>
          <SelectorInfoPanel text={`⚔️ Fights Left: ${fightersRemaining}`} />
          <Button
            variant="contained"
            onClick={handleResetChanges}
            disabled={!hasUnsavedChanges}
            sx={{
              minWidth: { xs: '90px', sm: '100px' },
              padding: { xs: '2px', sm: '2px 8px' },
              height: '32px',
              bgcolor: '#8b0000',
              color: 'white',
              border: '1px solid white',
              borderRadius: '4px',
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              fontFamily: 'Bebas Neue',
              fontWeight: 'normal',
              textTransform: 'none',
              letterSpacing: '0.5px',
              '&:hover': {
                bgcolor: '#8b0000',
              },
            }}
          >
            <RestartAltIcon
              sx={{
                marginRight: { xs: '4px', sm: '4px' },
                fontSize: { xs: '1.1rem', sm: '1.2rem' },
              }}
            />
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveChanges}
            disabled={!hasUnsavedChanges}
            sx={{
              minWidth: { xs: '90px', sm: '100px' },
              padding: { xs: '2px', sm: '2px 8px' },
              height: '32px',
              bgcolor: '#8b0000',
              color: 'white',
              border: '1px solid white',
              borderRadius: '6px',
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              fontFamily: 'Bebas Neue',
              fontWeight: 'normal',
              textTransform: 'none',
              letterSpacing: '0.5px',
              '&:hover': {
                bgcolor: '#8b0000',
              },
            }}
          >
            <SaveIcon
              sx={{
                marginRight: { xs: '4px', sm: '4px' },
                fontSize: { xs: '1.1rem', sm: '1.2rem' },
              }}
            />
            Save
          </Button>
        </Box>
      </Box>

      {/* Right Side Grid For Fight Picker */}
      <Box
        component={motion.div}
        variants={container}
        initial="hidden"
        animate="show"
        sx={{
          width: { xs: '100%', md: '40%' },
          marginTop: { xs: '10px', md: 0 },
          alignItems: 'center',
          justifyContent: 'flex-start',
          bgcolor: 'rgba(50, 50, 50, 0.8)',
          border: '1px solid white',
          borderRadius: '8px',
          overflowY: 'auto',
          overflowX: 'hidden',
          maxHeight: { xs: '450px', sm: '500px', md: 'calc(100vh - 100px)' },
          minHeight: { xs: '300px', sm: '350px' },
          display: 'flex',
          flexDirection: 'column',
          position: { xs: 'relative', md: 'sticky' },
          top: { xs: 'auto', md: '10px' },
          zIndex: 1,
        }}
      >
        <FighterPicker
          seasonFights={seasonFights}
          selectedCardId={selectedCardId}
          fighters={fighters}
          fighterInfoArray={fighterInfoArray}
          onFighterButtonClick={handleOpenModal}
        />
      </Box>

      <SelectorModal
        open={openModal}
        onClose={handleCloseModal}
        fighterInfo={modalFighterInfo}
        selectedFighterInfo={modalSelectedFighterInfo}
        onSwapFighters={handleSwapFighters}
      />

      <ReusableAlert
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        severity="warning"
        text={snackbarText}
      />
      {/* Loading Bar */}
      {(isUserSeasonLoading || isSeasonFightsLoading) && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(255,255,255,0.1)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#cc0000',
            },
          }}
        />
      )}

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Team Saved"
        message="Your team has been saved successfully!"
        autoHideDuration={3000}
      />

      {/* Confirmation Modal for Unsaved Changes */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => {
          setConfirmationModal({
            ...confirmationModal,
            isOpen: false,
          });
        }}
        onConfirm={() => {
          confirmationModal.onConfirm();
          setConfirmationModal({
            ...confirmationModal,
            isOpen: false,
          });
        }}
        title={confirmationModal.title}
        message={confirmationModal.message}
      />
    </Box>
  );
};

export default TeamSelector;
